import React from 'react'
import { Card, CardContent, Typography, Chip, Box, Avatar, CardHeader, CardMedia, CardActions, IconButton } from '@mui/material'
import { Favorite, FavoriteBorder, Share } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import type { Poem } from '../../../store/poemsSlice'
import { textWithLineBreaksFromHtml } from '../../../lib/sanitize'
import { useRecordShareMutation, useToggleLikeMutation } from '../../../store/apiSlice'
import { useAuth } from '../../auth/AuthProvider'
import { useToast } from '../../../hooks/useToast'
import ShareDialog from '../../../shared/components/ShareDialog'

const PoemCard: React.FC<{ poem: Poem; onClick?: () => void }> = ({ poem, onClick }) => {
  const { authenticated } = useAuth()
  const navigate = useNavigate()
  const { showSuccess } = useToast()
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false)
  const [toggleLikeFn] = useToggleLikeMutation()

  const [recordShareFn] = useRecordShareMutation()
  const snippet = React.useMemo(
    () => textWithLineBreaksFromHtml(poem.content),
    [poem.content]
  )

  return (
    <Card
      onClick={onClick}
      elevation={3}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
        background: (theme) => theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
        border: '1px solid',
        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252, 211, 77, 0.1)' : 'rgba(0,0,0,0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 12px 24px -10px rgba(0,0,0,0.6)'
            : '0 12px 24px -10px rgba(0,0,0,0.1)',
          borderColor: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
        }
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            sx={{
              bgcolor: (theme) => theme.palette.mode === 'dark' ? '#fcd34d' : '#d97706',
              color: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
              fontWeight: 'bold'
            }}
            aria-label="recipe"
          >
            {(poem.author || 'A').charAt(0).toUpperCase()}
          </Avatar>
        }
        title={
          <Typography variant="subtitle1" component="p" fontWeight="700" noWrap sx={{ maxWidth: 180 }}>
            {poem.author || 'Anonymous'}
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            {poem.createdAt ? new Date(poem.createdAt).toLocaleDateString() : 'Unknown Date'}
          </Typography>
        }
        sx={{ pb: 1 }}
      />

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Typography
          gutterBottom
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.3,
            height: '2.6em', // Fixed height for 2 lines
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {poem.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.6,
            height: '4.8em', // Fixed height for 3 lines
            overflow: 'hidden',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
          }}
          >
          {snippet}
        </Typography>
      </CardContent>

      <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={poem.language === 'ta' ? 'Tamil' : 'Latin'}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 24 }}
        />
        {poem.category && (
          <Chip
            label={poem.category}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        )}
      </Box>

      <CardActions disableSpacing sx={{ mt: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
        <IconButton
          aria-label="add to favorites"
          onClick={(e) => {
            e.stopPropagation()
            console.log('Like clicked. Authenticated:', authenticated, 'PoemID:', poem.id)
            if (!authenticated) {
              navigate('/login')
              return
            }
            toggleLikeFn(poem.id)
          }}
          sx={{
            color: poem.likedByCurrentUser ? 'error.main' : 'default'
          }}
        >
          {poem.likedByCurrentUser ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
        </IconButton>
        <IconButton
          aria-label="share"
          onClick={(e) => {
            e.stopPropagation()
            setShareDialogOpen(true)
          }}
        >
          <Share fontSize="small" />
        </IconButton>
      </CardActions>

      {shareDialogOpen && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          url={window.location.origin + `/poem/${poem.id}`}
          title={poem.title}
          onShareSelect={() => recordShareFn(poem.id)}
        />
      )}
    </Card>
  )
}

export default PoemCard
